import { useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Theme,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { useTranslation } from 'react-i18next';
import {
  uploadOrgAdminFile,
  type ProjectsApiOptions,
} from '../../api/projects';
import { type ContextFileTypeTag, type OrgAdminFile } from '../projects/types';

// Keep in sync with backend allowedExtensions whitelist
const ALLOWED_EXTENSIONS = new Set([
  '.a2l',
  '.arxml',
  '.c',
  '.capl',
  '.cpp',
  '.csv',
  '.dbc',
  '.docx',
  '.fibex',
  '.h',
  '.html',
  '.json',
  '.ldf',
  '.m',
  '.md',
  '.odx',
  '.pdf',
  '.py',
  '.reqif',
  '.reqifz',
  '.rst',
  '.txt',
  '.vbs',
  '.xlsx',
  '.xml',
  '.yaml',
]);

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const TYPE_TAG_OPTIONS: { value: ContextFileTypeTag; labelKey: string }[] = [
  {
    value: 'SYS2_REQUIREMENT',
    labelKey: 'uploadDialog.typeTags.SYS2_REQUIREMENT',
  },
  {
    value: 'SYS5_TEST_SCRIPT',
    labelKey: 'uploadDialog.typeTags.SYS5_TEST_SCRIPT',
  },
  { value: 'API_DESCRIPTOR', labelKey: 'uploadDialog.typeTags.API_DESCRIPTOR' },
  { value: 'GENERAL', labelKey: 'uploadDialog.typeTags.GENERAL' },
];

const EXTENSION_TO_TYPE_TAG: Record<string, ContextFileTypeTag> = {
  '.reqif': 'SYS2_REQUIREMENT',
  '.reqifz': 'SYS2_REQUIREMENT',
  '.capl': 'SYS5_TEST_SCRIPT',
  '.vbs': 'SYS5_TEST_SCRIPT',
  '.arxml': 'API_DESCRIPTOR',
  '.fibex': 'API_DESCRIPTOR',
  '.ldf': 'API_DESCRIPTOR',
  '.dbc': 'API_DESCRIPTOR',
  '.odx': 'API_DESCRIPTOR',
  '.a2l': 'API_DESCRIPTOR',
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const useStyles = makeStyles((theme: Theme) => ({
  dropZone: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.2s, background-color 0.2s',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
  },
  dropZoneActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
  },
  dropIcon: {
    fontSize: 40,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  field: {
    marginTop: theme.spacing(2),
  },
  progressBox: {
    marginTop: theme.spacing(2),
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  },
  fileInfoText: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  suggestionText: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
}));

type Props = {
  open: boolean;
  onClose: () => void;
  orgId: string;
  apiOptions: ProjectsApiOptions;
  isPreviewMode: boolean;
  onUploaded: (file: OrgAdminFile) => void;
};

export function OrgAdminUploadDialog({
  open,
  onClose,
  orgId,
  apiOptions,
  isPreviewMode,
  onUploaded,
}: Props) {
  const classes = useStyles();
  const { t } = useTranslation('projects');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [typeTag, setTypeTag] = useState<ContextFileTypeTag>('GENERAL');
  const [suggestedTypeTag, setSuggestedTypeTag] =
    useState<ContextFileTypeTag | null>(null);
  const [isDefault, setIsDefault] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);
  const [progress, setProgress] = useState<number | null>(null);
  const [validationError, setValidationError] = useState<string | undefined>();
  const [uploadError, setUploadError] = useState<string | undefined>();

  const reset = () => {
    setSelectedFile(null);
    setTypeTag('GENERAL');
    setSuggestedTypeTag(null);
    setIsDefault(false);
    setIsDragOver(false);
    dragCounterRef.current = 0;
    setProgress(null);
    setValidationError(undefined);
    setUploadError(undefined);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Called programmatically after a successful upload — always closes.
  const handleCloseAfterUpload = () => {
    reset();
    onClose();
  };

  // Called by user gesture (Escape, click-outside) — blocked during active upload.
  const handleClose = () => {
    if (progress !== null) return;
    reset();
    onClose();
  };

  const validateFile = (file: File): string | undefined => {
    if (file.size > MAX_FILE_SIZE) {
      return t('uploadDialog.errorFileTooLarge');
    }
    const ext = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
    if (!ALLOWED_EXTENSIONS.has(ext)) {
      return t('uploadDialog.errorInvalidType');
    }
    return undefined;
  };

  const applyFile = (file: File) => {
    setSelectedFile(file);
    setUploadError(undefined);
    setValidationError(validateFile(file));
    const ext = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
    const suggested = EXTENSION_TO_TYPE_TAG[ext] ?? null;
    setSuggestedTypeTag(suggested);
    if (suggested) setTypeTag(suggested);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) applyFile(file);
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleDragEnter = (event: React.DragEvent) => {
    event.preventDefault();
    dragCounterRef.current += 1;
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    dragCounterRef.current = 0;
    setIsDragOver(false);
    const file = event.dataTransfer.files?.[0];
    if (file) applyFile(file);
  };

  const handleDropZoneClick = () => {
    fileInputRef.current?.click();
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    const err = validateFile(selectedFile);
    if (err) {
      setValidationError(err);
      return;
    }

    setProgress(0);
    setUploadError(undefined);

    try {
      if (isPreviewMode) {
        for (let p = 0; p <= 100; p += 20) {
          setProgress(p);
          await new Promise(res => setTimeout(res, 100)); // eslint-disable-line no-promise-executor-return
        }
        const mockFile: OrgAdminFile = {
          id: `preview-${Date.now()}`,
          name: selectedFile.name,
          fileTypeTag: typeTag,
          status: 'PENDING',
          isDefault,
          version: 1,
          uploader: { userId: 0, displayName: t('members.selfName') },
          errorReport: null,
        };
        onUploaded(mockFile);
      } else {
        const result = await uploadOrgAdminFile(
          apiOptions,
          orgId,
          selectedFile,
          typeTag,
          isDefault,
          pct => setProgress(pct),
        );
        const newFile: OrgAdminFile = {
          id: result.fileId,
          name: selectedFile.name,
          fileTypeTag: typeTag,
          status: result.status,
          isDefault: result.isDefault,
          version: 1,
          uploader: null,
          errorReport: null,
        };
        onUploaded(newFile);
      }
      handleCloseAfterUpload();
    } catch {
      setUploadError(t('uploadDialog.uploadError'));
      setProgress(null);
    }
  };

  const isUploading = progress !== null;
  const canSubmit = !!selectedFile && !validationError && !isUploading;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('orgAdmin.uploadDialog.title')}</DialogTitle>
      <DialogContent>
        {/* Drag-and-drop zone */}
        <Box
          className={`${classes.dropZone} ${
            isDragOver ? classes.dropZoneActive : ''
          }`}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleDropZoneClick}
          role="button"
          tabIndex={0}
          onKeyDown={e =>
            (e.key === 'Enter' || e.key === ' ') && handleDropZoneClick()
          }
        >
          <CloudUploadIcon className={classes.dropIcon} />
          <Typography variant="body2">
            {t('orgAdmin.uploadDialog.dropZoneLabel')}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {t('orgAdmin.uploadDialog.dropZoneHint')}
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            onChange={handleFileChange}
          />
        </Box>

        {selectedFile && (
          <Typography variant="body2" className={classes.fileInfoText}>
            {selectedFile.name} — {formatBytes(selectedFile.size)}
          </Typography>
        )}
        {validationError && (
          <Typography variant="body2" className={classes.errorText}>
            {validationError}
          </Typography>
        )}

        <FormControl fullWidth variant="outlined" className={classes.field}>
          <InputLabel>{t('uploadDialog.typeTagLabel')}</InputLabel>
          <Select
            value={typeTag}
            onChange={e => setTypeTag(e.target.value as ContextFileTypeTag)}
            label={t('uploadDialog.typeTagLabel')}
          >
            {TYPE_TAG_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {suggestedTypeTag && (
          <Typography variant="caption" className={classes.suggestionText}>
            {t('orgAdmin.uploadDialog.typeSuggestion')}
          </Typography>
        )}

        <Box className={classes.field}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isDefault}
                onChange={e => setIsDefault(e.target.checked)}
                color="primary"
              />
            }
            label={t('orgAdmin.uploadDialog.isDefaultLabel')}
          />
          <Typography variant="caption" display="block" color="textSecondary">
            {t('orgAdmin.uploadDialog.isDefaultHint')}
          </Typography>
        </Box>

        {isUploading && (
          <Box className={classes.progressBox}>
            <Typography variant="body2">
              {t('uploadDialog.progress', { percent: progress })}
            </Typography>
            <LinearProgress variant="determinate" value={progress ?? 0} />
          </Box>
        )}

        {uploadError && (
          <Typography variant="body2" className={classes.errorText}>
            {uploadError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isUploading}>
          {t('actions.cancel')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={!canSubmit}
          onClick={handleUpload}
        >
          {t('uploadDialog.uploadButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
